import type { AgentResult, BehaviouralCounters } from "@cc/protocol";
import { DEFAULT_STATS } from "./config.js";

/**
 * A personality card is *observed*, never authored.
 *
 * The design docs are explicit about this: we must not hand the model a label
 * like "you are aggressive". Instead we measure what it actually did and let
 * the label emerge, so two runs of the same model can genuinely produce
 * different crickets.
 *
 * Axes are style descriptors, but they must not *reward failure*. An earlier
 * formula treated "traps stepped on" and "scout pulses fired" as pure upsides
 * on the radar, so a cricket that farmed more crystals then bled out on mines
 * painted a fuller polygon than the HP-preserving winner. Survival, trap-
 * penalised intel, and combat-only precision exist to keep the radar honest
 * about what actually decided the match (score + remaining HP).
 */
export interface PersonalityCard {
  readonly traits: readonly PersonalityTrait[];
  /** Emergent archetype name, e.g. "THE GAMBLER". */
  readonly archetype: string;
  readonly tagline: string;
}

export interface PersonalityTrait {
  readonly key: string;
  readonly label: string;
  /** Normalised 0..1, for the ██████░░ UI bars. */
  readonly value: number;
}

const TRAIT_KEYS = [
  "aggression",
  "exploration",
  "risk",
  "resourceFocus",
  "intel",
  "patience",
  "survival",
  "precision",
] as const;

type TraitKey = (typeof TRAIT_KEYS)[number];

export interface TraitAverages {
  aggression: number;
  exploration: number;
  risk: number;
  resourceFocus: number;
  /** Willingness to spend ticks on SCOUT pulses rather than on farming. */
  intel: number;
  patience: number;
  /** Remaining HP share — the half of the ranking total that farming alone ignores. */
  survival: number;
  precision: number;
}

/**
 * Trait bands for a reference 90-second match.
 *
 * Earlier bands were calibrated against 5-minute matches and against
 * `attacksAttempted` alone. After the match shortened to 90s — and after combat
 * items (bombs / blades) started dealing most of the damage — those formulas
 * pinned aggression, precision and intel near zero for almost every cricket,
 * regardless of how the fight actually went. The bands below are load-bearing
 * for the product promise that "the same model can produce a different cricket
 * every run": if a trait cannot move, the archetype vote collapses.
 */
const BANDS = {
  /** Damage dealt that counts as fully aggressive in a 90s match. */
  aggressionDamageMax: 240,
  /** Distance travelled (arena units) that counts as full roaming in 90s. */
  explorationDistMax: 1400,
  /** Traps stepped on that counts as maximally reckless in 90s. */
  riskMax: 4,
  /**
   * Combined intel volume: each deliberate SCOUT is worth 2, each trap noticed
   * (scout pulse or proximity) is worth 1. The final trait multiplies volume
   * by notice/(notice+stepped) so learning mines then walking onto them cannot
   * max the radar.
   */
  intelMax: 6,
  /** Damage taken that counts as zero survival when final HP is unavailable. */
  survivalDamageMax: 240,
  /** Reference match length the bands above assume. */
  referenceTicks: 900,
} as const;

export interface TraitFinals {
  readonly hp: number;
  readonly maxHp: number;
}

export function deriveTraits(
  counters: BehaviouralCounters,
  durationTicks: number,
  _arenaSize: number,
  finals?: TraitFinals,
): TraitAverages {
  // Stretch / compress bands with match length so a 30s skirmish and a 90s
  // duel remain readable on the same 0..1 radar.
  const scale = Math.max(0.35, durationTicks / BANDS.referenceTicks);

  /*
   * Aggression is *damage inflicted*, not melee swings.
   *
   * Counting only ATTACK attempts was dishonest once bombs and spinning blades
   * became the main way hunters chip HP: a cricket could deal 200+ damage and
   * still show aggression 0 because it never pressed the melee button.
   */
  const aggression = clamp01(counters.damageDealt / (BANDS.aggressionDamageMax * scale));

  /*
   * Exploration is how far the cricket actually ran, not how far from spawn it
   * once stood. Max-distance-from-spawn saturated near 1.0 for every profile on
   * a 100-unit map (walking to mid-map already cleared most of the band).
   */
  const exploration = clamp01(
    counters.distanceTravelled / (BANDS.explorationDistMax * scale),
  );

  const risk = clamp01(counters.trapsSteppedOn / (BANDS.riskMax * scale));

  const resourceFocus =
    counters.resourceFocusSamples === 0
      ? 0.5
      : clamp01(counters.resourceFocusSum / counters.resourceFocusSamples);

  /*
   * Intel is map knowledge that *paid off*. Volume (SCOUT pulses + notices)
   * is scaled by how often the cricket actually avoided the mines it learned:
   * trapsDetected / (trapsDetected + trapsSteppedOn). A "scout" who still
   * detonates five traps cannot paint a full radar while the cautious winner
   * looks blind.
   */
  const intelVolume = counters.scoutActions * 2 + counters.trapsDetected;
  const intelQuality =
    counters.trapsDetected + counters.trapsSteppedOn === 0
      ? counters.scoutActions > 0
        ? 0.6
        : 0
      : counters.trapsDetected /
        (counters.trapsDetected + counters.trapsSteppedOn);
  const intel = clamp01(intelVolume / (BANDS.intelMax * scale)) * intelQuality;

  const retreatShare = counters.retreatTicks / Math.max(1, counters.decisions);
  const patience = clamp01(retreatShare);

  /*
   * Survival is the ranking half that crystal count hides. A farmer who banks
   * fewer ◆ but keeps 210 HP outranks a mine-walker sitting on 371 ◆ / 46 HP;
   * the radar has to show that edge or viewers read the loser as "stronger".
   */
  const survival = finals
    ? clamp01(finals.hp / Math.max(1, finals.maxHp))
    : clamp01(1 - counters.damageTaken / (BANDS.survivalDamageMax * scale));

  /*
   * Precision is combat hit rate only.
   *
   * Falling back to collection success made every non-fighter look like a
   * sharpshooter ("命中率 91" with zero swings), which inflated the radar for
   * farmers who never aimed. No swings → neutral 0.5. Small samples shrink
   * toward neutral so two lucky punches cannot crown a duelist.
   */
  const attempted = counters.attacksAttempted;
  const precision =
    attempted > 0
      ? clamp01(shrinkRate(counters.attacksLanded, attempted))
      : 0.5;

  return {
    aggression,
    exploration,
    risk,
    resourceFocus,
    intel,
    patience,
    survival,
    precision,
  };
}

/** Bayesian-ish shrink of a rate toward 0.5 so n=2 cannot dominate the radar. */
function shrinkRate(successes: number, trials: number, prior = 0.5, strength = 4): number {
  return (successes + prior * strength) / (trials + strength);
}

export function derivePersonality(
  result: AgentResult,
  durationTicks: number,
  arenaSize: number,
): PersonalityCard {
  const traits = deriveTraits(result.behavioural, durationTicks, arenaSize, {
    hp: result.hp,
    maxHp: DEFAULT_STATS.maxHp,
  });

  const traitList: PersonalityTrait[] = [
    { key: "aggression", label: "Aggression", value: traits.aggression },
    { key: "exploration", label: "Exploration", value: traits.exploration },
    { key: "risk", label: "Risk taking", value: traits.risk },
    { key: "resourceFocus", label: "Resource focus", value: traits.resourceFocus },
    { key: "intel", label: "Scouting", value: traits.intel },
    { key: "patience", label: "Patience", value: traits.patience },
    { key: "survival", label: "Survival", value: traits.survival },
    { key: "precision", label: "Precision", value: traits.precision },
  ];

  const { archetype, tagline } = classify(traits);
  return { traits: traitList, archetype, tagline };
}

/**
 * The archetype is chosen by the *dominant* trait pair rather than a decision
 * tree, so a model drifts between archetypes as its behaviour changes instead
 * of being permanently boxed in.
 *
 * Each candidate is scored by how far its defining trait pair sits above a
 * "this is genuinely who I am" midpoint, and the strongest claim wins. An
 * earlier version was a first-match-wins decision tree, which looked reasonable
 * but was quietly broken: `THE SCOUT`'s condition (`exploration > 0.6 &&
 * aggression < 0.4`) is satisfied by *any* non-combatant that walked away from
 * spawn, and it was tested early, so every passive cricket was branded a scout
 * no matter how it actually played. Scoring also removes the ordering
 * sensitivity that made the label depend on where a branch happened to sit.
 */
function classify(t: TraitAverages): { archetype: string; tagline: string } {
  const {
    aggression,
    exploration,
    risk,
    resourceFocus,
    intel,
    patience,
    survival,
    precision,
  } = t;

  /** How strongly a pair of traits defines a personality, above 0.5 neutral. */
  const score = (a: number, b: number) => a - 0.5 + (b - 0.5);

  const candidates: { archetype: string; tagline: string; score: number }[] = [
    {
      archetype: "THE BERSERKER",
      tagline: "Attacks first, thinks never.",
      score: score(aggression, risk),
    },
    {
      archetype: "THE DUELIST",
      tagline: "Picks its fights, and wins them.",
      score: score(aggression, precision),
    },
    {
      archetype: "THE GAMBLER",
      tagline: "Always believes the next crystal is worth it.",
      score: score(risk, resourceFocus),
    },
    {
      archetype: "THE SCOUT",
      tagline: "Knows the map better than the enemy does.",
      // Requires *actual* scouting / trap notice, not merely having walked a long
      // way. Distance alone made this label dominate.
      score: score(intel, exploration),
    },
    {
      archetype: "THE TURTLE",
      tagline: "Refuses to lose the fight it never takes.",
      // Needs *both* defensive tempo and HP left. Survival alone would brand
      // every full-HP farmer a turtle; patience alone let low-HP runners claim it.
      score: score(patience, survival),
    },
    {
      archetype: "THE COLLECTOR",
      tagline: "Sees only the crystals.",
      score: score(resourceFocus, 1 - aggression),
    },
    {
      archetype: "THE PHILOSOPHER",
      tagline: "Conserves energy with great conviction.",
      score: score(1 - aggression, 1 - exploration),
    },
  ];

  // Below this, no pair is pronounced enough to name a personality honestly.
  const DECISIVE = 0.12;
  const best = candidates.reduce((a, b) => (b.score > a.score ? b : a));
  if (best.score < DECISIVE) {
    return { archetype: "THE ALL-ROUNDER", tagline: "No obvious weakness. No obvious plan either." };
  }
  return { archetype: best.archetype, tagline: best.tagline };
}

export function clamp01(v: number): number {
  if (Number.isNaN(v)) return 0;
  return Math.min(1, Math.max(0, v));
}

/** Renders a trait as a ten-cell bar, matching the design docs. */
export function renderBar(value: number, cells = 10): string {
  const filled = Math.round(clamp01(value) * cells);
  return "█".repeat(filled) + "░".repeat(cells - filled);
}

export function renderPersonalityCard(card: PersonalityCard, title: string): string {
  const lines = [title, "━".repeat(12), ""];
  for (const trait of card.traits) {
    const label = trait.label.padEnd(14, " ");
    lines.push(`${label} ${renderBar(trait.value)}`);
  }
  lines.push("", `${card.archetype}`, `  ${card.tagline}`);
  return lines.join("\n");
}

export { TRAIT_KEYS };
export type { TraitKey };
